// Group-key construction for the report layer, with the failure that cost a week built in.
//
// ⛔ WHY THIS EXISTS. Portfolio Tax Trend grouped on `${row.property_id}|${row.tax_year}` while
// buildParcelYearRows never emitted property_id. Every parcel in the client keyed to
// "undefined|2021", collapsed into ONE bucket, took the label of whichever property sorted first,
// and reported the entire client's assessed value and taxes as that property's. It looked
// plausible for weeks: a property with no bills at all showed unasterisked tax figures, and the
// year-over-year swings read as real market movement.
//
// Nothing failed. No error, no warning, no empty result — a missing field silently became a valid
// bucket name. That is the whole failure mode, and it is unreachable now:
//
//   UNDEFINED means the field DOES NOT EXIST on the row -> a programming error -> THROW.
//   NULL means the field exists and has no value -> legitimate data -> allowed, keyed as ''.
//
// The distinction is the point. A parcel with no property_id is a data condition worth grouping;
// a row that never carried property_id at all is a bug, and it should stop rather than average.
//
// Throwing is deliberate over warning. ReportView catches a buildRows throw, logs it and renders
// an empty table, so the worst case is a visibly empty report with a console error naming the
// field — recoverable, and impossible to mistake for an answer. A warning would leave the wrong
// figures on screen, which is what already happened.

// Build a group key from named fields, refusing to invent one from a field that isn't there.
export function groupKey(row, fields, context = 'report') {
  return (fields || []).map((f) => {
    const v = row?.[f];
    if (v === undefined) {
      throw new Error(
        `[${context}] cannot group: field "${f}" is UNDEFINED on the row. `
        + `A missing field must never become part of a bucket name — that is how one property's `
        + `row came to hold an entire client's figures. Row keys present: ${Object.keys(row || {}).join(', ')}`
      );
    }
    return v === null ? '' : String(v);
  }).join('|');
}

// groupBy over named fields, validated. Returns Map<key, rows[]>.
export function groupRowsBy(rows, fields, context = 'report') {
  const m = new Map();
  (rows || []).forEach((r) => {
    const k = groupKey(r, fields, context);
    const list = m.get(k);
    if (list) list.push(r); else m.set(k, [r]);
  });
  return m;
}

// A definition's own assertion that the fields it reads are actually on the rows it built.
// ⛔ Checks the FIRST row only, and deliberately so: it is a shape check, not a data check. Every
// row from one builder has the same keys, and scanning all of them would turn a cheap guard into
// a per-render cost. Call it once, at the top of buildRows, after the rows exist.
export function assertRowFields(rows, fields, context = 'report') {
  if (!rows || !rows.length) return rows;
  const missing = (fields || []).filter((f) => rows[0][f] === undefined);
  if (missing.length) {
    throw new Error(
      `[${context}] the row shape is missing ${missing.length === 1 ? 'a field' : 'fields'} this report reads: `
      + `${missing.join(', ')}. Present: ${Object.keys(rows[0]).join(', ')}`
    );
  }
  return rows;
}

// A filter whose source field exists on NO row silently matches everything (undefined reads as
// blank, and the (None) chip is lit by default), so a misspelled `from:` looks like a working
// filter that does nothing. Same class as the group-key bug; warns rather than throws, because a
// filter is presentation and an over-broad one is not a wrong number.
export function warnOnDeadFilters(rows, filters, context = 'report') {
  if (!rows || !rows.length) return;
  (filters || []).forEach((f) => {
    if (f.type !== 'enum' && f.type !== 'search') return;
    const keys = f.type === 'search' ? (f.keys || [f.key]) : [f.from || f.key];
    keys.forEach((k) => {
      if (rows.every((r) => r[k] === undefined)) {
        console.warn(
          `[${context}] filter "${f.label || f.key}" reads field "${k}", which exists on NO row. `
          + 'It will appear to work and filter nothing. Check the `from:` against the row shape.'
        );
      }
    });
  });
}
